







































































































































import Vue from 'vue';
import {
  email,
  minLength,
  required,
  requiredIf,
} from 'vuelidate/lib/validators';
import { ECountryIso } from '@ringana/configs';
import {
  differenceInYears,
  parse,
  subYears,
  startOfToday,
} from 'date-fns';
import { DatepickerValidationMode, Datepicker, IDatepickerErrorObject } from '@ringana/datepicker';
import { EInputIdentifier } from '@/common/enums/input-identifier.enum';
import FetchHelper from '../../../helpers/OldFetchHelper';
import TextInput from '../inputs/TextInput.vue';
import CountrySelectInput from '../inputs/CountrySelectInput.vue';
import SalutationSelect from '../inputs/SalutationSelect.vue';
import Checkbox from '../inputs/Checkbox.vue';
import CurrencySelect from '../inputs/CurrencySelect.vue';
import CustomerHelper from '../../../helpers/CustomerHelper';
import LanguageSelect from '../inputs/LanguageSelect.vue';
import OldErrorHandlerHelper from '../../../helpers/OldErrorHandlerHelper';
import ValidationHelper from '../../../helpers/ValidationHelper';
import { IAddress } from '../../../common/interfaces/shared.interface';
import config from '../../../../config';
import ErrorHandlerHelper from '@/helpers/ErrorHandlerHelper';

const checkAge = (value: string): boolean => {
  const dateToValidate = parse(value, `dd.MM.yyyy`, new Date());
  return differenceInYears(new Date(), dateToValidate) >= 18;
};

function checkSwissSocialSecurityNumber(value: string): boolean {
  // I don't know how 'this' works here since it's outside of the component scope, therefore I won't touch it.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if(!(this.isSwissCustomer && this.isPartner) || !this.checkForSwissCustomer) {
    return true;
  }
  return ValidationHelper.checkSwissSocialSecurityNumber(value);
}

interface IProfileBaseDataFormComponent {
  DatepickerValidationMode: typeof DatepickerValidationMode;
  EInputIdentifier: typeof EInputIdentifier;
  customerUpdateData: { [index: string]: unknown };
  datepicker: {
    months: string[] | number[];
    max: number;
  };
  displayNationalityField: boolean;
  isSwissCustomer: boolean;
  maxLengthForSecurityNumber: number;
  swissSocialSecurityNumberPlaceholder: string;
}

export default Vue.extend({
  name: `ProfileBaseDataForm`,
  components: {
    Checkbox,
    CountrySelectInput,
    CurrencySelect,
    Datepicker,
    LanguageSelect,
    SalutationSelect,
    TextInput,
  },
  data(): IProfileBaseDataFormComponent {
    return {
      DatepickerValidationMode,
      EInputIdentifier,
      customerUpdateData: {},
      datepicker: {
        max: subYears(startOfToday(), 18).getTime(),
        months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
      },
      displayNationalityField: false,
      isSwissCustomer: false,
      maxLengthForSecurityNumber: 16,
      swissSocialSecurityNumberPlaceholder: `756.xxxx.xxxx.xx`,
    };
  },
  props: {
    checkForSwissCustomer: {
      default: false,
      type: Boolean,
    },
    customerData: {
      default: () => { return {}; },
      type: Object as () => { [index: string]: unknown },
    },
    datepickerClearable: {
      default: false,
      type: Boolean,
    },
    isPartner: {
      default: false,
      type: Boolean,
    },
    isPartnerView: {
      default: false,
      type: Boolean,
    },
    isSelfRegistration: {
      default: false,
      type: Boolean,
    },
  },
  async mounted(): Promise<void> {
    window.$swal = this.$swal;
    this.initMonthStrings();
    if(CustomerHelper.getLoggedInState()) {
      await this.fetchCustomerAddresses();
    }
  },
  methods: {
    async fetchCustomerAddresses(): Promise<void> {
      const customerQuery = this.customerData.customerNumber ? `?customerNumber=${this.customerData.customerNumber}` : ``;
      const response = await FetchHelper.get({ path: `/customers/addresses/${customerQuery}` });
      const deliveryAddressCountryISO = response.data.find((a: IAddress) => a.deliveryDefault).countryISO;
      if(deliveryAddressCountryISO === ECountryIso.CH) {
        this.isSwissCustomer = true;
      }
      if(deliveryAddressCountryISO === ECountryIso.CH || deliveryAddressCountryISO === ECountryIso.FR) {
        this.displayNationalityField = true;
      }
    },
    handleDatepickerError($event: IDatepickerErrorObject): void {
      ErrorHandlerHelper.handleError($event.info, {
        logToConsole: true,
      });
    },
    initMonthStrings(): void {
      this.datepicker.months = config.datepicker.months.map(key => this.$t(key).toUpperCase());
    },
    resetValidations(): void {
      this.$v.$reset();
    },
    validate(): void {
      this.$v.$touch();
      if(!this.$v.customerUpdateData?.birthDate?.checkAge) {
        OldErrorHandlerHelper.handleError(this.$t(`my-profile:profileBaseDataForm.errorAgeVerification`), { showSwal: true });
      }
      if(this.isSwissCustomer && this.checkForSwissCustomer && !this.$v.customerUpdateData?.socialSecurityNumber?.checkSwissSocialSecurityNumber) {
        OldErrorHandlerHelper.handleError(this.$t(`my-profile:profileBaseDataForm.socialSecurityNumberError`), { showSwal: true });
      }
    },
    valueChanged(): void {
      this.$emit(`change`, JSON.parse(JSON.stringify(this.customerUpdateData)));
      this.$emit(`validate`, !this.$v.$anyError);
    },
  },
  watch: {
    customerData: {
      deep: true,
      handler(): void {
        this.customerUpdateData = JSON.parse(JSON.stringify(this.customerData));
      },
      immediate: true,
    },
    customerUpdateData(): void {
      if(this.isSwissCustomer) {
        this.customerUpdateData.socialSecurityNumber = (this.customerUpdateData.socialSecurityNumber as string).replace(/[^0-9]/g, ``)
          .replace(/^(\d{3})?(\d{4})?(\d{4})?(\d{2})/g, `$1.$2.$3.$4`);
      }
    },
  },
  validations: {
    customerUpdateData: {
      birthDate: {
        checkAge,
        required,
      },
      email: {
        email,
        required,
      },
      firstName: {
        minLength: minLength(2),
        required,
      },
      lastName: {
        minLength: minLength(2),
        required,
      },
      socialSecurityNumber: {
        checkSwissSocialSecurityNumber,
        required: requiredIf(function isSwissCustomer() {
          // I don't know how 'this' works here since it's outside of the component scope, therefore I won't touch it.
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          return this.isSwissCustomer && this.checkForSwissCustomer;
        }),
      },
    },
  },
});
